import { useEffect, useMemo } from "react";
import { X } from "lucide-react";

import type { ApiConversation, ApiMessage, ApiUser } from "../lib/api";
import { isGroupedContinuation } from "../lib/grouping";
import { useThreadDetail } from "../lib/hooks";
import { Message } from "./Message";

interface ThreadDrawerProps {
  teamID: string;
  convID: string;
  users: Record<string, ApiUser>;
  conversations: ApiConversation[];
  avatarPaletteByUserID: Record<string, number>;
  /** ts of the parent message we want a thread for. null = closed. */
  parentTs: string | null;
  /** Parent message we already have in memory (from the messages
   *  page). When provided we render it immediately while the
   *  thread fetch is in flight, so the drawer doesn't flash an
   *  empty state. */
  fallbackParent: ApiMessage | null;
  onClose: () => void;
  /** Pass 2.12: pixel-native width set by MessagePane from
   *  useLayoutState. Was previously a hardcoded w-[420px] class
   *  alongside position:absolute; now driven by the resizable
   *  layout AND the drawer is a flex sibling of the message body
   *  (not an overlay). */
  width: number;
}

// Pass 2.12: refactored from absolute-positioned slide-in overlay
// to a flex sibling of the message body. Slack itself does the
// flex-sibling treatment (drawer pushes content left, doesn't
// cover it); the pre-2.12 overlay was a hand-me-down from the
// pre-thin-client viewer where the drawer needed to float over
// a self-rendered message canvas.
//
// Pre-2.12 artifacts removed:
//   absolute top-0 bottom-0 right-0 z-20 shadow-drawer
//   transition-transform translate-x-* pointer-events-none
//   data-state="open|closed"   (drawer is rendered XOR not — no
//                               animation state to expose)
//   w-[420px]                  (now driven by inline style from
//                               useLayoutState)
//
// MessagePane handles the conditional render — this component is
// only mounted when there's actually a parent_ts to display, so
// `parentTs` is structurally never null at this point. The prop
// stays nullable for the useEffect's KeyboardEvent guard, which
// short-circuits in case a stale parent rerender slips through.
//
// Pass 2.2: thread data fetched via /threads/{parent_ts}. We
// have the parent already (from MessagePane's messages page) so
// we render it immediately; the API fetch supplies replies.
export function ThreadDrawer({
  teamID,
  convID,
  users,
  conversations,
  avatarPaletteByUserID,
  parentTs,
  fallbackParent,
  onClose,
  width,
}: ThreadDrawerProps) {
  const isOpen = !!parentTs;
  const { data: thread, error } = useThreadDetail(
    teamID,
    convID,
    parentTs,
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const parent = thread?.parent ?? fallbackParent;
  const replies: ApiMessage[] = thread?.replies ?? [];

  const replyItems = useMemo(() => {
    let prev: { user: string | undefined; ts: string } | null = null;
    return replies.map((reply, i) => {
      const continuation = isGroupedContinuation(prev, {
        user: reply.user ?? undefined,
        ts: reply.ts,
      });
      prev = { user: reply.user ?? undefined, ts: reply.ts };
      return { reply, key: reply.ts || `${parentTs}-r${i}`, continuation };
    });
  }, [replies, parentTs]);

  return (
    <aside
      role="dialog"
      aria-label="Thread"
      data-testid="thread-drawer"
      // Pass 2.12: pixel-native width via inline style, no Tailwind
      // w-* class. flex-shrink-0 prevents the drawer from being
      // crushed when the message body grows.
      style={{ width }}
      className="bg-theme-bg-message-pane border-l border-theme-border-default flex flex-col flex-shrink-0 min-w-0"
    >
      <header className="px-4 py-3 border-b border-theme-border-default flex items-center justify-between flex-shrink-0">
        <div className="min-w-0">
          <h3 className="font-serif text-base font-semibold text-theme-text-primary tracking-tightish">
            Thread
          </h3>
          {(() => {
            const conv = conversations.find((c) => c.id === convID);
            if (!conv) return null;
            const prefix =
              conv.type === "public_channel" || conv.type === "private_channel"
                ? "# "
                : "";
            return (
              <div
                data-testid="thread-drawer-subtitle"
                className="font-mono text-[10px] text-theme-text-muted truncate"
              >
                {prefix}
                {conv.label}
              </div>
            );
          })()}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close thread drawer"
          data-testid="thread-drawer-close"
          className="w-7 h-7 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-theme-text-tertiary hover:text-theme-text-primary transition-colors duration-150"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
        </button>
      </header>
      {parent && (
        <div className="flex-1 overflow-y-auto py-2 scrollbar-thin">
          <Message
            teamID={teamID}
            msg={parent}
            users={users}
            conversations={conversations}
            avatarPaletteByUserID={avatarPaletteByUserID}
            onOpenThread={() => {}}
          />
          <div className="border-t border-theme-border-default my-2" />
          {error && (
            <p className="px-4 py-2 font-mono text-xs text-theme-error-text">
              Could not load replies: {error.message}
            </p>
          )}
          {replyItems.map(({ reply, key, continuation }) => (
            <Message
              key={key}
              teamID={teamID}
              msg={reply}
              users={users}
              conversations={conversations}
              avatarPaletteByUserID={avatarPaletteByUserID}
              indented
              groupedContinuation={continuation}
              onOpenThread={() => {}}
            />
          ))}
        </div>
      )}
    </aside>
  );
}
